class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);


/* ============================================================
   VF / Pebble & Wick — Cinematic Details Disclosure Enhancements
   Safe layer on top of Dawn details-disclosure.js.
   Keeps dropdown/menu focus-out behavior and header positioning intact.
   ============================================================ */

(() => {
  const DetailsDisclosureElement = customElements.get('details-disclosure');
  const HeaderMenuElement = customElements.get('header-menu');

  if (!DetailsDisclosureElement || DetailsDisclosureElement.prototype.__vfCinematicPatched) return;

  DetailsDisclosureElement.prototype.__vfCinematicPatched = true;

  const originalDisclosureOnToggle = DetailsDisclosureElement.prototype.onToggle;
  const originalDisclosureClose = DetailsDisclosureElement.prototype.close;
  const originalDisclosureOnFocusOut = DetailsDisclosureElement.prototype.onFocusOut;

  const root = document.documentElement;

  const getSummary = (disclosure) => disclosure.mainDetailsToggle?.querySelector('summary');

  const setDisclosureState = (disclosure, isOpen, detail = {}) => {
    const summary = getSummary(disclosure);

    disclosure.classList.toggle('vf-details-disclosure-open', isOpen);
    disclosure.classList.toggle('vf-details-disclosure-closed', !isOpen);
    disclosure.dataset.vfDisclosureState = isOpen ? 'open' : 'closed';

    if (summary) {
      summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      summary.classList.toggle('vf-summary-open', isOpen);
    }

    disclosure.dispatchEvent(
      new CustomEvent(isOpen ? 'vf:details-disclosure-opened' : 'vf:details-disclosure-closed', {
        bubbles: true,
        detail
      })
    );
  };

  const enhanceDisclosure = (disclosure) => {
    if (!disclosure || disclosure.__vfCinematicEnhanced) return;

    disclosure.__vfCinematicEnhanced = true;
    disclosure.classList.add('vf-details-disclosure-enhanced');

    const summary = getSummary(disclosure);
    if (summary) {
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', disclosure.mainDetailsToggle?.hasAttribute('open') ? 'true' : 'false');
      summary.classList.add('vf-details-disclosure__summary');
    }

    if (disclosure.content) {
      disclosure.content.classList.add('vf-details-disclosure__content');
    }

    setDisclosureState(disclosure, Boolean(disclosure.mainDetailsToggle?.hasAttribute('open')), {
      initial: true
    });
  };

  DetailsDisclosureElement.prototype.onToggle = function vfDisclosureOnToggle(event) {
    const isOpen = this.mainDetailsToggle?.hasAttribute('open');

    this.classList.add('vf-details-disclosure-toggling');

    const result = originalDisclosureOnToggle.call(this, event);

    setDisclosureState(this, Boolean(isOpen), {
      trigger: event?.target || this.mainDetailsToggle,
      disclosure: this
    });

    window.setTimeout(() => {
      this.classList.remove('vf-details-disclosure-toggling');
    }, 320);

    return result;
  };

  DetailsDisclosureElement.prototype.onFocusOut = function vfDisclosureOnFocusOut(event) {
    this.classList.add('vf-details-disclosure-focusout');

    const result = originalDisclosureOnFocusOut.call(this, event);

    window.setTimeout(() => {
      this.classList.remove('vf-details-disclosure-focusout');
    }, 220);

    return result;
  };

  DetailsDisclosureElement.prototype.close = function vfDisclosureClose() {
    this.classList.add('vf-details-disclosure-closing');

    const result = originalDisclosureClose.call(this);

    setDisclosureState(this, false, {
      disclosure: this
    });

    window.setTimeout(() => {
      this.classList.remove('vf-details-disclosure-closing');
    }, 260);

    return result;
  };

  if (HeaderMenuElement && !HeaderMenuElement.prototype.__vfCinematicPatched) {
    HeaderMenuElement.prototype.__vfCinematicPatched = true;

    const originalHeaderOnToggle = HeaderMenuElement.prototype.onToggle;
    const originalHeaderClose = HeaderMenuElement.prototype.close;

    HeaderMenuElement.prototype.onToggle = function vfHeaderMenuOnToggle(event) {
      const isOpen = this.mainDetailsToggle?.hasAttribute('open');

      this.classList.add('vf-header-menu-toggling');
      root.classList.toggle('vf-header-menu-open', Boolean(isOpen));
      document.body?.classList.toggle('vf-header-menu-open', Boolean(isOpen));

      const summary = getSummary(this);
      if (summary) summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      const result = originalHeaderOnToggle.call(this, event);

      setDisclosureState(this, Boolean(isOpen), {
        trigger: event?.target || this.mainDetailsToggle,
        headerMenu: true
      });

      window.setTimeout(() => {
        this.classList.remove('vf-header-menu-toggling');
      }, 320);

      return result;
    };

    HeaderMenuElement.prototype.close = function vfHeaderMenuClose() {
      this.classList.add('vf-header-menu-closing');

      const result = originalHeaderClose.call(this);

      root.classList.remove('vf-header-menu-open');
      document.body?.classList.remove('vf-header-menu-open');
      setDisclosureState(this, false, {
        headerMenu: true
      });

      window.setTimeout(() => {
        this.classList.remove('vf-header-menu-closing');
      }, 260);

      return result;
    };
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('details-disclosure, header-menu').forEach(enhanceDisclosure);
  }, { once: true });

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('details-disclosure summary, header-menu summary');
    if (!summary) return;

    summary.classList.add('vf-summary-clicked');

    window.setTimeout(() => {
      summary.classList.remove('vf-summary-clicked');
    }, 420);
  });

  document.addEventListener('keyup', (event) => {
    if (event.code?.toUpperCase() !== 'ESCAPE') return;

    document.querySelectorAll('details-disclosure, header-menu').forEach((disclosure) => {
      if (!disclosure.mainDetailsToggle?.hasAttribute('open')) return;

      disclosure.classList.add('vf-details-disclosure-escape');
      window.setTimeout(() => {
        disclosure.classList.remove('vf-details-disclosure-escape');
      }, 360);
    });
  });
})();
